import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Movie } from '../../types/Movie';
import { User } from '../../services/authService';
import { movieService } from '../../services/movieService';
import './MovieCard.css';

interface MovieCardProps {
  movie: Movie;
  user: User | null;
  onEdit: (movie: Movie) => void;
  onDelete: (id: string) => void;
}

const MovieCard: React.FC<MovieCardProps> = ({ movie, user, onEdit, onDelete }) => {
  const navigate = useNavigate();
  const [showActionMenu, setShowActionMenu] = useState(false);
  const [isCreatingNote, setIsCreatingNote] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setShowActionMenu(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleEdit = () => {
    setShowActionMenu(false);
    onEdit(movie);
  };

  const handleDelete = () => {
    setShowActionMenu(false);
    onDelete(movie.id);
  };

  const handleViewDetails = () => {
    setShowActionMenu(false);
    navigate(`/movies/${movie.id}`);
  };

  const handleViewNotes = async () => {
    setShowActionMenu(false);
    if (!user || isCreatingNote) return;
    
    setIsCreatingNote(true);
    try {
      // Check if note already exists
      let noteId = await movieService.getMovieNoteId(movie.id);
      
      // If note doesn't exist, create it
      if (!noteId) {
        noteId = await movieService.createMovieNote(movie.id, user.id, movie.name);
      }
      
      // Navigate to note
      if (noteId) {
        navigate(`/notes/${noteId}`);
      }
    } catch (error) {
      console.error('Error accessing movie notes:', error);
    } finally {
      setIsCreatingNote(false);
    }
  };

  return (
    <div className="movie-card">
      <div className="movie-content">
        {/* Poster on the left */}
        <div className="movie-poster-container">
          {movie.posterUrl ? (
            <img 
              src={movie.posterUrl} 
              alt={`${movie.name} poster`}
              className="movie-poster"
              onError={(e) => {
                const target = e.target as HTMLImageElement;
                target.style.display = 'none';
              }}
            />
          ) : (
            <div className="movie-poster-placeholder">
              🎬
            </div>
          )}
        </div>

        {/* Movie details on the right */}
        <div className="movie-details">
          <div className="movie-info">
            <h3 className="movie-name">{movie.name}</h3>
            {movie.director && (
              <p className="movie-director">by {movie.director}</p>
            )}
            {movie.year && (
              <p className="movie-year">{movie.year}</p>
            )}
            {movie.genre && (
              <p className="movie-genre">{movie.genre}</p>
            )}
            {movie.rating && (
              <p className="movie-rating">⭐ {movie.rating}/5</p>
            )}
            {movie.duration && (
              <p className="movie-duration">{movie.duration} min</p>
            )}
            {movie.status && (
              <div className="movie-status-badge">
                {movie.status === 'want_to_watch' && '📋 Want to Watch'}
                {movie.status === 'watching' && '🎬 Watching'}
                {movie.status === 'completed' && '✅ Completed'}
                {movie.status === 'paused' && '⏸️ Paused'}
                {movie.status === 'abandoned' && '❌ Abandoned'}
              </div>
            )}
          </div>
        </div>

        {/* Action menu */}
        <div className="movie-actions" ref={menuRef}>
          <button 
            className="action-menu-trigger"
            onClick={() => setShowActionMenu(!showActionMenu)}
            title="More actions"
          >
            ⋮
          </button>
          
          {showActionMenu && (
            <div className="action-menu">
              <button 
                className="action-menu-item"
                onClick={handleViewDetails}
              >
                📄 View Details
              </button>
              <button 
                className="action-menu-item"
                onClick={handleViewNotes}
                disabled={isCreatingNote || !user}
              >
                {isCreatingNote ? '⏳' : '📝'} View Notes
              </button>
              <button 
                className="action-menu-item"
                onClick={handleEdit}
              >
                ✏️ Edit
              </button>
              <button 
                className="action-menu-item action-menu-item-danger"
                onClick={handleDelete}
              >
                🗑️ Delete
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default MovieCard;

